import { Mesh, IcosahedronGeometry, ShaderMaterial, DoubleSide, Color, LinearSRGBColorSpace } from 'three';

/**
 * Ground projected env map adapted from @react-three/drei.
 * https://github.com/pmndrs/drei/blob/master/src/core/Environment.tsx
 */


export class GroundProjectedSkybox2 extends Mesh {
	
	constructor( texture, options = {} ) {
		
		const isCubeMap = texture.isCubeTexture;
		
		const defines = [
			isCubeMap ? '#define ENVMAP_TYPE_CUBE' : ''
		];
		
		const vertexShader = /* glsl */ `
			varying vec3 vWorldPosition;
			varying mat4 vModelMatrix;
			varying float z;
			
			void main() {
				vec4 worldPosition = ( modelMatrix * vec4( position, 1.0 ));
				vWorldPosition = worldPosition.xyz;
				vModelMatrix = modelMatrix;
				gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
				z = -gl_Position.z;
			}
			`;
		const fragmentShader = defines.join( '\n' ) + /* glsl */ `

				varying vec3 vWorldPosition;
				varying mat4 vModelMatrix;

				uniform float radius;
				uniform float height;
				uniform float angle;
				uniform float rotation;
				
				uniform float t;
				uniform vec3 fogColor;
				uniform float fogDensity;
				varying float z;
				uniform vec3 clouds;
				uniform float cloudsScale;
				uniform float cloudsDark;
				uniform float cloudsLight;
				uniform float cloudsCover;
				uniform float cloudsAlpha;
				uniform float cloudsTint;
				uniform float horizon;
				uniform float uTime;
				uniform float uSpeed;
				uniform float cloudsActive;

				#ifdef ENVMAP_TYPE_CUBE

					uniform samplerCube map;
					uniform samplerCube map2;
					uniform samplerCube alphaMap;

				#else

					uniform sampler2D map;
					uniform sampler2D map2;
					uniform sampler2D alphaMap;

				#endif

				uniform bool useAlphaMap;

				// From: https://www.shadertoy.com/view/4tsBD7
				float diskIntersectWithBackFaceCulling( vec3 ro, vec3 rd, vec3 c, vec3 n, float r )
				{

					float d = dot ( rd, n );

					if( d > 0.0 ) { return 1e6; }

					vec3 o = ro - c;
					float t = - dot( n, o ) / d;
					vec3 q = o + rd * t;

					return ( dot( q, q ) < r * r ) ? t : 1e6;

				}

				// From: https://www.iquilezles.org/www/articles/intersectors/intersectors.htm
				float sphereIntersect( vec3 ro, vec3 rd, vec3 ce, float ra ) {

					vec3 oc = ro - ce;
					float b = dot( oc, rd );
					float c = dot( oc, oc ) - ra * ra;
					float h = b * b - c;

					if( h < 0.0 ) { return -1.0; }

					h = sqrt( h );

					return - b + h;

				}
				
				vec3 project() {

					vec3 p = normalize( vWorldPosition);
					vec3 camPos = cameraPosition;
					float camDistance = distance(camPos.y,height) / 2.0;
					float sphereHeight = clamp(camDistance, height,height + camDistance);
					camPos.y -= sphereHeight;

					float intersection = sphereIntersect( camPos, p, vec3( 0.0 ), radius );
					if( intersection > 0.0 ) {

						vec3 h = vec3( 0.0, -sphereHeight, 0.0 );
						float intersection2 = diskIntersectWithBackFaceCulling( camPos, p, h, vec3( 0.0, 1.0, 0.0 ), radius );
						p = ( camPos + min( intersection, intersection2 ) * p ) / radius;

					} else {

						p = vec3( 0.0, 1.0, 0.0 );

					}

					return p;

				}

				#include <common>
				#include <clipping_planes_pars_fragment>
				
				const mat2 m = mat2( 1.6,  1.2, -1.2,  1.6 );
				const float pi = 3.141592653589793238462643383279502884197169;
				
				float hash(vec2 uv) {
				    return fract(sin(dot(uv, vec2(124.0, 678.0))) * 987654.0);
				}
				
				float noise(vec2 uv) {
				    vec2 i = floor(uv);
				    vec2 f = fract(uv);
				    vec2 b = smoothstep(0.0, 1.0, f);
				    return 2.0 * mix(
				    mix(hash(i), hash(i + vec2(1.0, 0.0)), b.x),
				    mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), b.x), b.y) - 1.0;
				}
				
				float fbm(bool do_abs, vec2 n, float amp_init, float amp_step, float q, float base_octave) {
				    //float time = uTime * 0.002 * 0.03 * base_octave;
				
				    n *= vec2(1.0) * cloudsScale * base_octave;
				//        n -= q - cloudsProgress;
				
				    n -= q;
				
				    float total = 0.0, amplitude = amp_init;
				    for (int i = 0; i < 7; i++) {
				        float v = noise(n) * amplitude;
				        v = do_abs ? abs(v) : v;
				
				        total += v;
				
				//        n = m * n + cloudsProgress;
				        n = m * n;
				        amplitude *= amp_step;
				    }
				    return total;
				}
				
				float fbmManual(bool do_abs, vec2 n, float amp_init, float amp_step, float q, float base_octave) {
				    //float time = uTime * 0.002 * 0.03 * base_octave;
				
				    n *= vec2(1.0) * 2.7 * base_octave;
				    //    n -= q - time;
				    n -= q;
				
				    float total = 0.0, amplitude = amp_init;
				    for (int i = 0; i < 7; i++) {
				        float v = noise(n) * amplitude;
				        v = do_abs ? abs(v) : v;
				
				        total += v;
				
				        //n = m * n + time;
				        n = m * n;
				        amplitude *= amp_step;
				    }
				    return total;
				}
				
				float calcF(vec2 p, float q) {
				    //ridged noise shape
				    float r = fbm(true, p, 0.8, 0.7, q, 1.0);
				
				    //noise shape
				    float f = fbm(false, p, 0.7, 0.6, q, 1.0);
				
				    f *= r + f;
				
				    f = cloudsCover + cloudsAlpha * f * r;
				
				    return f;
				}
				float calcC(vec2 p, float q) {
				    //noise colour
				    float c = fbm(false, p, 0.4, 0.6, q, 2.0);
				
				    //noise ridge colour
				    float c1 = fbm(true, p, 0.4, 0.6, q, 3.0);
				
				    return c + c1;
				}
				
				void main() {

					vec3 projectedWorldPosition = (vec4(project(),0.0) * vModelMatrix).xyz;
					vec4 diffuseColor = vec4(0.0,0.0,0.0,1.0);
					vec3 outgoingLight1 = vec3(0.0);
					vec3 outgoingLight2 = vec3(0.0);
					vec3 outgoingLight = vec3(0.0);

					#ifdef ENVMAP_TYPE_CUBE

						outgoingLight1 = textureCube( map, projectedWorldPosition ).rgb;
						outgoingLight2 = textureCube( map2, projectedWorldPosition ).rgb;
						outgoingLight = mix(outgoingLight1,outgoingLight2,t);
						if (useAlphaMap) {
							diffuseColor.a = mix(textureCube(alphaMap, projectedWorldPosition).r, 1.0, t);
						}

					#else

						vec3 direction = normalize( projectedWorldPosition );
						vec2 uv = equirectUv( direction );
						outgoingLight1 = texture2D( map, uv ).rgb;
						outgoingLight2 = texture2D( map2, uv ).rgb;
						outgoingLight = mix(outgoingLight1,outgoingLight2,t);

					#endif
					
					float fogDepth = z;
                    float fogFactor = 1.0 - exp(-fogDensity * fogDensity * fogDepth * fogDepth);
                    outgoingLight = mix(outgoingLight, fogColor, fogFactor);
					
					#include <opaque_fragment>
					
					if (cloudsActive == 1.0) {
				        vec4 fragLut = gl_FragColor;
				        vec3 direction = normalize( vWorldPosition - vec3( 0.0, 0.0, 0.0 ) );
				
				        float theta = acos( direction.y );
				        float phi = atan( direction.z, direction.x );
				        vec2 cloudsUV = vec2( phi, theta ) * 4.0;
				        float time = uTime * uSpeed;
				        cloudsUV.x *= 0.5;
				        cloudsUV.x -= time;
				
				        float q = fbm(false, cloudsUV, 0.1, 0.4, 0.0, 1.0);
				        float f = calcF(cloudsUV, q);
				        float c = calcC(cloudsUV, q);
				
				        vec3 cloudcolour = clouds * clamp((cloudsTint + cloudsDark + cloudsLight * c), 0.0, 1.0);
				        vec4 mixed = mix( fragLut, vec4(cloudcolour,1.0), clamp(f + c, 0.0, 1.0) );
				
				        vec4 finalColor = mix(mixed,fragLut,smoothstep(0.0, 1.0, cloudsUV.y - horizon ));
				
				        finalColor = mix(fragLut,finalColor,smoothstep(0.0, 1.0, cloudsUV.x + 2.0 * pi + time));
				        finalColor = mix(finalColor,fragLut,smoothstep(0.0, 1.0, cloudsUV.x - 1.7 * pi + time));
				
				        gl_FragColor = finalColor;
				    }
					
					
					#include <tonemapping_fragment>
					#include <colorspace_fragment>
				}
				`;
		
		const uniforms = {
			map: { value: texture },
			map2        : { value: null        },
			alphaMap    : { value: null        },
			useAlphaMap : { value: false       },
			t           : { value: 0.0         },
			fogColor    : { value: new Color() },
			fogDensity  : { value: 0.0         },
			height: { value: options.height || 15 },
			radius: { value: options.radius || 100 },
			cloudsActive: {value: 0.0},
			clouds: {value: new Color().setStyle('#ffffff',LinearSRGBColorSpace)},
			cloudsScale: {value: 2.7},
			cloudsDark: {value: 0.0},
			cloudsLight: {value: 0.16},
			cloudsCover: {value: 0.5},
			cloudsAlpha: {value: 4.0},
			cloudsTint: {value: 0.91},
			horizon: {value: 0.0},
			uSpeed: {value: 0.0},
			uTime: {value: 0.0}
		};
		
		const geometry = new IcosahedronGeometry( 1, 16 );
		const material = new ShaderMaterial( {
			uniforms,
			fragmentShader,
			vertexShader,
			side: DoubleSide,
			depthWrite: false,
			transparent: true
		} );
		
		super( geometry, material );
		
	}
	
	set radius( radius ) {
		
		this.material.uniforms.radius.value = radius;
		
	}
	
	get radius() {
		
		return this.material.uniforms.radius.value;
		
	}
	
	set height( height ) {
		
		this.material.uniforms.height.value = height;
		
	}
	
	get height() {
		
		return this.material.uniforms.height.value;
		
	}
	
	set secondTexture(texture) {
		this.material.uniforms.map2.value = texture;
	}

	set alphaMap( alphaMap ) {

		this.material.uniforms.alphaMap.value = alphaMap;
		this.material.uniforms.useAlphaMap.value = Boolean(alphaMap);

	}
	
}
